/**
 * ColorPalette is used to create a color palette widget with MATLAB standard colors
 * or your own custom colors.
 *
 * @module mw-colorpicker/ColorPalette
 * @mixes dijit/_WidgetBase
 * @mixes dijit/_TemplatedMixin
 * @mixes dijit/_PaletteMixin
 *
 * @example <caption>Simple MATLAB Color Palette</caption>
 * var colorPalette = new ColorPalette({
 *      onChange: function (newColor) {console.log(newColor)}
 * });
 * colorPalette.placeAt(<some dom node>);
 * colorPalette.startup();
 *
 * @copyright Copyright 2015 MathWorks, Inc.
 */
define([
    'dojo/_base/declare', // declare
    'dojo/dom-construct', // domConstruct.place
    'dojo/string', // string.substitute
    'dojo/dom-attr',
    'dojo/dom-style',
    'dojo/on',
    'dojo/query',
    'dijit/_PaletteMixin',
    'dijit/_TemplatedMixin',
    'dijit/_WidgetBase',
    './MColor',
    'dojo/text!./templates/ColorPalette.html',
    'dojo/text!./templates/ColorPaletteSwatch.html',
    'dojo/i18n!./l10n/nls/ColorPickerResources'
], function (declare, domConstruct, string, domAttr, domStyle, on, query,
    _PaletteMixin, _TemplatedMixin, _WidgetBase, MColor, template, swatchTemplate, l10nStr) {
    var cp = declare([_WidgetBase, _TemplatedMixin, _PaletteMixin], {

        /**
         * @property {string[]} palette - array of hex color string
         * Default colors are provided by
         * getStandardColors in {@link module:mw-colorpicker/MColor}
         *
         * @instance
         */
        palette: [],

        /**
         * @property {string} title - Title to be shown on top of the palette
         * @instance
         */
        title: l10nStr.standardColors,

        showTitle: true,

        _setTitleAttr: {
            node: 'titleNode',
            type: 'innerText'
        },

        baseClass: 'mwColorPalette',

        templateString: template,

        constructor: function () {
            this._set('palette', MColor.getStandardColors());
            this.inherited(arguments);
        },

        buildRendering: function () {
            // Instantiate the template, which makes a skeleton into which we'll insert a bunch of
            // div nodes
            this.inherited(arguments);

            this.dyeClass = cp._Color;

            // Creates dom nodes in each cell of the template.
            this._preparePalette(this.palette, {});
        },

        startup: function () {
            this.inherited(arguments);
            this._addPreviewChangeEventListener();
        },

        _addPreviewChangeEventListener: function () {
            var paletteCells = this.gridNode.querySelectorAll('.dijitPaletteCell');
            for (var i = 0; i < paletteCells.length; i++) {
                var cell = paletteCells[i];
                cell.addEventListener('mouseenter', this._emitPreviewChangeEvent.bind(this));
                cell.addEventListener('mouseleave', this._emitPreviewChangeEvent.bind(this));
                cell.addEventListener('focus', this._emitPreviewChangeEvent.bind(this));
                cell.addEventListener('blur', this._emitPreviewChangeEvent.bind(this));
            }
        },

        _removePreviewChangeEventListener: function () {
            var paletteCells = this.gridNode.querySelectorAll('.dijitPaletteCell');
            for (var i = 0; i < paletteCells.length; i++) {
                var cell = paletteCells[i];
                cell.removeEventListener('mouseenter', this._emitPreviewChangeEvent.bind(this));
                cell.removeEventListener('mouseleave', this._emitPreviewChangeEvent.bind(this));
                cell.removeEventListener('focus', this._emitPreviewChangeEvent.bind(this));
                cell.removeEventListener('blur', this._emitPreviewChangeEvent.bind(this));
            }
        },

        _setShowTitleAttr: function (newVal) {
            this._set('showTitle', newVal);
            if (newVal) {
                this.titleNode.classList.remove('mwColorPaletteTitleHidden');
            } else {
                this.titleNode.classList.add('mwColorPaletteTitleHidden');
            }
        },

        _emitPreviewChangeEvent: function (e) {
            var color;
            if (e.type === 'mouseenter' || e.type === 'focus') {
                color = this._getPaletteCellColor(e.target);
            }
            this.emit('previewchange', {
                value: color,
                eventData: e
            });
        },

        _getPaletteCellColor: function (element) {
            var paletteSwatchElement = element.getElementsByClassName('mwColorPaletteSwatch')[0];
            return new MColor(paletteSwatchElement.style.backgroundColor).toHex();
        },

        destroy: function () {
            this._removePreviewChangeEventListener();
            this.inherited(arguments);
        }

    });

    cp._Color = declare([MColor], {

        template: swatchTemplate,

        getValue: function () {
            return this.toHex();
        },

        fillCell: function (cell) {
            var html = string.substitute(this.template, {
                // substitution variables for normal mode
                color: this.toHex(),
                title: this.toMATLABFriendlyName()
            });
            domAttr.set(cell, 'title', this.toMATLABFriendlyName());
            domConstruct.place(html, cell);
        }
    });

    return cp;
});
